import React from 'react';
import {
  FDBodyText,
  FDBox, FDCard, FDCardActions, FDCardContent,
  FDContentContainer, FDHeadlineText, FDIcon, FDSecondaryActionButton,
  FDStack, FDTitleText,
} from '@facet-wealth/design';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import MetaFormInline from 'Components/UI/Meta/MetaForm/Components/MetaFormInline';
import { buildFacetDesignOverride } from 'Packages/FacetRest/FacetDesignOverride';
import { facetFacUrl } from 'Consts/logo';
import PropTypes from 'prop-types';

import fr from 'facet-rest';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';

export const getOutcomeSummaryDetailIcon = outcomeType => {
  switch (outcomeType) {
    case 'education':
      return 'group';
    case 'buyAProperty':
      return 'home';
    case 'sellAProperty':
      return 'home';
    case 'generalSavings':
      return 'beachAccess';
    case 'retirement':
      return 'trendingUp';
    default:
      return 'trendingUp';
  }
};

// TODO: This might be a good candidate for a shared component if we want family information to look the same
const OutcomeSummaryItemCard = (props) => {
  const {
    // eslint-disable-next-line no-unused-vars
    avatarSrc,
    // eslint-disable-next-line no-unused-vars
    avatarAlt,
    text,
    outcomeType,
    bodyText,
    buttonLabel,
    buttonOnClick,
  } = props;

  return (
    <FDCard width={293} height={252}>
      <FDCardContent flexGrow={1}>
        <FDStack direction={'column'} spacing={2} alignItems={'center'} flexGrow={1}>
          {/* <FDAvatar alt={avatarAlt} src={avatarSrc} size={'lg'} /> */}
          <FDIcon icon={getOutcomeSummaryDetailIcon(outcomeType)} size={'lg'} color="primary" />
          <FDStack direction={'column'} spacing={0} alignItems={'center'}>
            <FDTitleText color={'textSecondary'} value={text} size={'md'} />
            <FDBodyText value={bodyText} size={'sm'} />
          </FDStack>
        </FDStack>
      </FDCardContent>
      <FDCardActions>
        <FDStack direction={'row'} spacing={0} alignItems={'center'} justifyContent={'center'} flexGrow={1}>
          <FDSecondaryActionButton
            label={buttonLabel}
            size={'sm'}
            flexShrink={0}
            onClick={buttonOnClick}
          />
        </FDStack>
      </FDCardActions>
    </FDCard>
  );
};

OutcomeSummaryItemCard.defaultProps = {
  outcomeType: '',
  avatarSrc: facetFacUrl,
  avatarAlt: '',
  text: '',
  bodyText: '',
  buttonLabel: 'View Details',
  buttonOnClick: () => {},
};

OutcomeSummaryItemCard.propTypes = {
  outcomeType: PropTypes.string,
  avatarSrc: PropTypes.string,
  avatarAlt: PropTypes.string,
  text: PropTypes.string,
  bodyText: PropTypes.string,
  buttonLabel: PropTypes.string,
  buttonOnClick: PropTypes.func,
};

const DesiredOutcomesSummaryPageSectionLayout = () => {
  const { resource } = useMetaLayoutContext();
  const id = resource.id();

  const { loadModalCollectionByLink } = useModalContext();

  return (
    <React.Fragment key={id}>
      <FDStack direction={'column'} spacing={4}>
        <FDStack flexGrow={1}>
          <FDBox>
            <FDHeadlineText color={'textSecondary'} size="sm" value={resource.attr('title') ?? ''} />
          </FDBox>
          {resource.withRelationship(fr.relationships.addItem.name, (rel) => {
            return (
              <>
                <FDStack flexGrow={1} />
                <FDSecondaryActionButton
                  label={rel.meta('prompt') || 'Add Outcome'}
                  onClick={() => {
                    loadModalCollectionByLink(rel.link('related').url());
                  }}
                  size={'sm'}
                />
              </>
            );
          }, () => null)}
        </FDStack>
        <FDStack direction={'row'} spacing={4}>
          {resource.attr('summaryItems').resources().list().map((summaryItem) => (
            <OutcomeSummaryItemCard
              outcomeType={summaryItem.attr('outcomeType')}
              avatarSrc={''}
              avatarAlt={''}
              text={summaryItem.attr('outcomeName')}
              bodyText={summaryItem.attr('outcomeSubtitle') ?? ''}
              buttonLabel={summaryItem.withRelationship(fr.relationships.details.name,
                (rel) => rel.meta('prompt'),
                () => 'View Details')}
              buttonOnClick={summaryItem.withRelationship(fr.relationships.details.name,
                (rel) => () => {
                  loadModalCollectionByLink(rel.link('related').url());
                },
                () => {})}

            />
          ))}
        </FDStack>
      </FDStack>
    </React.Fragment>
  );
};

const desiredOutcomesSummaryPageSectionLayoutMetaOverrideSet = buildFacetDesignOverride();

const DesiredOutcomesSummaryLayout = () => {
  const { resource } = useMetaLayoutContext();
  const sectionsResources = resource.attr('sections').resources().list();

  return (
    <FDBox margin={600}>
      <FDContentContainer maxWidth={'xl'}>
        <FDStack direction={'column'} flex={1} flexGrow={1} spacing={6}>
          <FDStack>
            <FDStack flexGrow={1}>
              <FDBox>
                <FDHeadlineText color={'textSecondary'} size="md" value={resource.attr('title')} />
              </FDBox>
            </FDStack>
          </FDStack>
          {sectionsResources.map(section => {
            return (
              <MetaFormInline
                resource={section}
                layout={DesiredOutcomesSummaryPageSectionLayout}
                metaOverrideSet={desiredOutcomesSummaryPageSectionLayoutMetaOverrideSet}
                fullWidth
                padding={'none'}
                noIndividualSubmitButtons
              />
            );
          })}
        </FDStack>
      </FDContentContainer>
    </FDBox>
  );
};

const desiredOutcomesSummaryLayoutMetaOverrideSet = buildFacetDesignOverride();

const DesiredOutcomesSummaryPageTemplate = () => {
  const { pageContentResource: resource } = usePageContentContext();
  if (!resource) {
    return null;
  }

  return (
    <MetaFormInline
      resource={resource}
      layout={DesiredOutcomesSummaryLayout}
      metaOverrideSet={desiredOutcomesSummaryLayoutMetaOverrideSet}
      fullWidth
      padding={'none'}
      noIndividualSubmitButtons
    />
  );
};

DesiredOutcomesSummaryPageTemplate.defaultProps = {
};

DesiredOutcomesSummaryPageTemplate.propTypes = {
};

export default DesiredOutcomesSummaryPageTemplate;
